import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Globe, Briefcase, ClipboardList, UserCog, GraduationCap,
  LineChart, Sparkles, LogOut, Bell, Search, MessagesSquare, LifeBuoy,
  ChevronDown, Settings, ExternalLink, Wallet, CalendarDays, ShieldCheck, Calendar, Rocket,
  Wand2, StickyNote, BookOpen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AIAssistantPanel from "@/components/AIAssistantPanel";
import Avatar from "@/components/Avatar";
import apiClient from "@/api/client";

const ALL_NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, end: true, testid: "nav-dashboard", key: null },
  { to: "/musteriler", label: "Müşteriler", icon: Users, testid: "nav-customers", key: "members_can_see_customers" },
  { to: "/onboarding", label: "Onboarding", icon: Rocket, testid: "nav-onboarding", key: "members_can_see_customers" },
  { to: "/hizmetler", label: "Hizmetler", icon: Briefcase, testid: "nav-services", key: "members_can_see_services" },
  { to: "/gorevler", label: "Görevler", icon: ClipboardList, testid: "nav-tasks", key: "members_can_see_tasks" },
  { to: "/toplantilar", label: "Toplantı & Leadler", icon: CalendarDays, testid: "nav-meetings", key: "members_can_see_meetings" },
  { to: "/takvim", label: "Takvim", icon: Calendar, testid: "nav-calendar", key: "members_can_see_calendar" },
  { to: "/notlar", label: "Notlar", icon: StickyNote, testid: "nav-notes", key: null },
  { to: "/destek", label: "Destek", icon: LifeBuoy, testid: "nav-tickets", key: "members_can_see_tickets" },
  { to: "/mesajlar", label: "Mesajlar", icon: MessagesSquare, testid: "nav-messages", key: "members_can_see_messages" },
  { to: "/domainler", label: "Domain Takibi", icon: Globe, testid: "nav-domains", key: "members_can_see_domains" },
  { to: "/ogrenciler", label: "Öğrenciler", icon: GraduationCap, testid: "nav-students", key: "members_can_see_students" },
  { to: "/dersler", label: "Dersler", icon: BookOpen, testid: "nav-lessons", key: "members_can_see_students" },
  { to: "/ekip", label: "Ekip", icon: UserCog, testid: "nav-team", key: "members_can_see_team" },
  { to: "/google-ads", label: "Google Ads MCC", icon: LineChart, testid: "nav-ads", key: "members_can_see_ads" },
  { to: "/ai-araclar", label: "AI Araçlar", icon: Wand2, testid: "nav-ai-tools", key: null },
  { to: "/finans", label: "Gelir & Gider", icon: Wallet, testid: "nav-finance", key: "members_can_see_finance" },
  { to: "/yonetim", label: "Yönetim", icon: ShieldCheck, testid: "nav-admin", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [aiOpen, setAiOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState({ customers: [], tasks: [] });
  const location = useLocation();
  const nav = useNavigate();
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const [perms, setPerms] = useState({});

  useEffect(() => {
    apiClient.get("/me/permissions").then(r => setPerms(r.data || {})).catch(() => {});
  }, []);

  const NAV = ALL_NAV.filter(item => {
    if (item.adminOnly) return isAdmin;
    if (!item.key) return true; // Dashboard always shown
    return isAdmin || !!perms[item.key];
  });

  const loadNotifs = async () => {
    try {
      const { data } = await apiClient.get("/notifications");
      setNotifs(data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults({ customers: [], tasks: [] }); return; }
    const id = setTimeout(async () => {
      try {
        const [cs, ts] = await Promise.all([
          apiClient.get(`/customers?q=${encodeURIComponent(searchQ)}`).then(r => r.data),
          apiClient.get("/tasks").then(r => r.data),
        ]);
        setSearchResults({
          customers: (cs || []).slice(0, 5),
          tasks: (ts || []).filter(t => t.title.toLowerCase().includes(searchQ.toLowerCase())).slice(0, 5),
        });
      } catch {}
    }, 200);
    return () => clearTimeout(id);
  }, [searchQ]);

  const unread = notifs.filter(n => !n.is_read).length;

  const markAll = async () => {
    await apiClient.post("/notifications/read-all");
    loadNotifs();
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFB] text-[#0F172A]">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-60 bg-white border-r border-[#ECECEF] flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-[#ECECEF]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center text-white font-bold text-sm">KY</div>
            <div>
              <div className="text-[13px] font-semibold leading-none">Kamil Yıldırım</div>
              <div className="text-[10px] text-[#94A3B8] mt-0.5">Dijital Pazarlama CRM</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-[13px] mb-0.5 transition-colors ${
                  isActive
                    ? "bg-[#F4F4F5] text-[#0F172A] font-medium"
                    : "text-[#64748B] hover:bg-[#F4F4F5] hover:text-[#0F172A]"
                }`
              }
            >
              <item.icon size={15} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#ECECEF]">
          <button onClick={() => setAiOpen(true)} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-white text-[13px] font-medium hover:opacity-90 transition" data-testid="open-ai-assistant">
            <Sparkles size={14} />
            <span>Akıllı Asistan</span>
            <span className="ml-auto w-1.5 h-1.5 bg-white rounded-full ky-pulse" />
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-[#ECECEF] flex items-center px-4 lg:px-6 gap-3 sticky top-0 z-30">
          <button onClick={() => setSearchOpen(true)} className="flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-md border border-[#ECECEF] bg-[#FAFAFB] text-[13px] text-[#94A3B8] hover:border-[#DEDEE3] transition" data-testid="topbar-search">
            <Search size={14} />
            <span>Müşteri, görev ara…</span>
            <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-[#ECECEF] text-[#64748B]">⌘K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-2">
            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button onClick={() => setNotifOpen(o => !o)} className="ky-btn ky-btn-ghost ky-btn-icon relative" data-testid="topbar-notifications">
                <Bell size={16} />
                {unread > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-[16px] text-[9px] font-bold bg-[#EF4444] text-white rounded-full flex items-center justify-center px-1">{unread}</span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-[360px] ky-card overflow-hidden z-40 ky-fade-in" data-testid="notif-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#ECECEF]">
                    <div className="text-sm font-semibold">Bildirimler</div>
                    {unread > 0 && <button onClick={markAll} className="text-xs text-[#4F46E5] hover:underline" data-testid="mark-all-read">Tümünü oku</button>}
                  </div>
                  <div className="max-h-[420px] overflow-y-auto">
                    {notifs.length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]">Bildirim yok.</div>}
                    {notifs.slice(0, 10).map((n) => (
                      <button
                        key={n.id}
                        onClick={() => { apiClient.post(`/notifications/${n.id}/read`).then(loadNotifs); if (n.link) nav(n.link); setNotifOpen(false); }}
                        className={`w-full text-left px-4 py-3 border-b border-[#ECECEF] hover:bg-[#FAFAFB] ${!n.is_read ? "bg-[#EEF2FF]/30" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.is_read && <span className="ky-dot bg-[#4F46E5] mt-1.5" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-[#0F172A]">{n.title}</div>
                            <div className="text-[11px] text-[#94A3B8] mt-0.5">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => { setNotifOpen(false); nav("/bildirimler"); }} className="w-full px-4 py-2.5 text-xs text-[#4F46E5] hover:bg-[#FAFAFB] border-t border-[#ECECEF]">Tüm bildirimleri gör</button>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button onClick={() => setProfileOpen(o => !o)} className="flex items-center gap-2 h-9 px-2 pr-3 rounded-md hover:bg-[#F4F4F5] transition" data-testid="topbar-profile">
                <Avatar name={user?.name} color={user?.avatar_color} size={26} />
                <div className="hidden md:block text-left">
                  <div className="text-[12px] font-medium leading-none">{user?.name}</div>
                  <div className="text-[10px] text-[#94A3B8] leading-none mt-0.5">{user?.role === "admin" ? "Admin" : "Ekip Üyesi"}</div>
                </div>
                <ChevronDown size={12} className="text-[#94A3B8]" />
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-[240px] ky-card overflow-hidden z-40 ky-fade-in" data-testid="profile-dropdown">
                  <div className="px-4 py-3 border-b border-[#ECECEF]">
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-xs text-[#64748B] mt-0.5 truncate">{user?.email}</div>
                    {user?.title && <div className="text-[11px] text-[#94A3B8] mt-1">{user.title}</div>}
                  </div>
                  <button onClick={() => { setProfileOpen(false); nav("/bildirimler"); }} className="w-full flex items-center gap-2 px-4 py-2 text-[13px] hover:bg-[#FAFAFB] text-left">
                    <Bell size={14} /> Bildirimler
                  </button>
                  <a href="/portal/login" target="_blank" rel="noreferrer" className="w-full flex items-center gap-2 px-4 py-2 text-[13px] hover:bg-[#FAFAFB] text-left">
                    <ExternalLink size={14} /> Müşteri Portalı
                  </a>
                  <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-[#EF4444] hover:bg-[#FEF2F2] text-left border-t border-[#ECECEF]" data-testid="logout-btn">
                    <LogOut size={14} /> Çıkış yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <div className="max-w-[1400px] mx-auto p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-24" onClick={() => setSearchOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl ky-card overflow-hidden" data-testid="search-modal">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#ECECEF]">
              <Search size={16} className="text-[#94A3B8]" />
              <input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Müşteri, görev, ekip…" className="flex-1 bg-transparent focus:outline-none text-sm" data-testid="search-input" />
              <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#F4F4F5] text-[#64748B]">ESC</kbd>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {searchResults.customers.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#94A3B8] bg-[#FAFAFB]">Müşteriler</div>
                  {searchResults.customers.map(c => (
                    <button key={c.id} onClick={() => { setSearchOpen(false); nav(`/musteriler/${c.id}`); }} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#FAFAFB] text-left">
                      <Users size={14} className="text-[#94A3B8]" />
                      <div>
                        <div className="text-sm">{c.name}</div>
                        <div className="text-xs text-[#94A3B8]">{c.company}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {searchResults.tasks.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#94A3B8] bg-[#FAFAFB]">Görevler</div>
                  {searchResults.tasks.map(t => (
                    <button key={t.id} onClick={() => { setSearchOpen(false); nav("/gorevler"); }} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#FAFAFB] text-left">
                      <ClipboardList size={14} className="text-[#94A3B8]" />
                      <div className="text-sm">{t.title}</div>
                    </button>
                  ))}
                </div>
              )}
              {searchQ && searchResults.customers.length === 0 && searchResults.tasks.length === 0 && (
                <div className="p-6 text-center text-sm text-[#94A3B8]">Sonuç bulunamadı.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <AIAssistantPanel open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
